import React, { useMemo } from 'react';
import { useFormatCellValue } from 'apps/sigma2-ui/src/explore-section/hooks/useFormatCellValue';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { NumericDisplayUnit } from 'libs/end-to-end/src/metrics';
import { OverallDisplayMode } from 'libs/end-to-end/src/view-configurations';

interface GenericTooltipProps {
  header: React.ReactNode;
  body: React.ReactNode;
  footer?: React.ReactNode;
}

export const GenericTooltip: React.FC<{ children: React.ReactNode }> = (
  props,
) => {
  return (
    <div className="bg-white p-2 shadow-md border rounded flex flex-col gap-2">
      {props.children}
    </div>
  );
};

export const GenericTooltipStructure: React.FC<GenericTooltipProps> = ({
  header,
  body,
  footer,
}) => {
  return (
    <div className="flex flex-col gap-2">
      {header}
      <div className="w-full border-t-1 border-gray-200" />
      <div className="flex items-center gap-2 justify-between">{body}</div>
      {footer && (
        <div className="px-2 py-2 rounded-md bg-gray-100">{footer}</div>
      )}
    </div>
  );
};

interface SingleMetricTooltipProps {
  metricColumn: string;
  color?: string;
  levelDisplayName: string;
  percentageValue: number;
  totalSubjects: number;
  numeratorSize?: number;
  units?: NumericDisplayUnit;
  overallDisplayMode?: OverallDisplayMode;
}

export const SingleMetricTooltip: React.FC<SingleMetricTooltipProps> = (
  props,
) => {
  return (
    <GenericTooltip>
      <SingleMetricTooltipContent {...props} />
    </GenericTooltip>
  );
};

export const SingleMetricTooltipContent: React.FC<SingleMetricTooltipProps> = ({
  metricColumn,
  color,
  percentageValue,
  numeratorSize,
  levelDisplayName,
  totalSubjects,
  units,
  overallDisplayMode,
}) => {
  const { getColumnDisplayName } = useGetColumnDisplayName();

  const { formatCellValue } = useFormatCellValue();

  const language = useLanguage();

  const title = useMemo(() => {
    return getColumnDisplayName(metricColumn);
  }, [metricColumn, getColumnDisplayName]);

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  const metricDefinition = useMemo(() => {
    return getMetricAndFocusAreaFromColumnName({
      columnName: metricColumn,
    })?.metric;
  }, [metricColumn, getMetricAndFocusAreaFromColumnName]);

  const [primaryValue, secondaryValue] = useMemo(() => {
    const displayOverallAverage = overallDisplayMode !== 'count';

    const primaryValue = formatCellValue({
      rawValue: String(percentageValue),
      header: metricColumn,
      units,
    });

    const secondaryValue = String(numeratorSize);

    if (displayOverallAverage) {
      return [primaryValue, secondaryValue];
    }

    return [secondaryValue, primaryValue];
  }, [
    overallDisplayMode,
    formatCellValue,
    metricColumn,
    numeratorSize,
    percentageValue,
    units,
  ]);

  const body = useMemo(() => {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2 text-nowrap mr-4">
            {color && (
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: color }}
              />
            )}
            <S2Typography
              variant="headline-6-alt"
              translatedContent={levelDisplayName}
            />
          </div>
          <div className="flex items-center">
            <S2Typography
              variant="headline-5-alt"
              translatedContent={primaryValue}
            />
          </div>
        </div>
        <div className="px-2 py-1 rounded-md bg-gray-100">
          <S2Typography
            variant="body-7"
            translatedContent={
              numeratorSize
                ? language.subjectsCount({
                    subjectsCount: secondaryValue,
                    totalSample: String(totalSubjects),
                    whiteLabelingSubjectPlural:
                      metricDefinition?.whiteLabeling?.subjectPlural ?? null,
                  })
                : language.totalSubjects({ totalSubjects })
            }
          />
        </div>
      </div>
    );
  }, [
    color,
    levelDisplayName,
    primaryValue,
    numeratorSize,
    language,
    secondaryValue,
    totalSubjects,
    metricDefinition?.whiteLabeling?.subjectPlural,
  ]);

  return (
    <GenericTooltipStructure
      header={
        <S2Typography variant="headline-7-alt" translatedContent={title} />
      }
      body={body}
    />
  );
};
